import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  NotificationsActive as NotificationIcon,
  LocationOn as LocationIcon,
  PhotoCamera as CameraIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  HelpOutline as HelpIcon,
  ContentCopy as CopyIcon,
  PhoneIphone as DeviceIcon,
  GetApp as InstallIcon,
} from '@mui/icons-material';
import { subscribeToPush } from '../../components/Notification/PushNotificationSetup';
import { isStandalone, usePwaInstall } from '../../utils/functions/pwaInstall';

// สถานะ: granted | denied | prompt | unsupported | unknown (เบราว์เซอร์ไม่ให้ query เช่น Safari/iOS)
// tone = ชื่อสีใน theme.palette ที่ใช้ตกแต่งการ์ด
const STATUS_META = {
  granted: { label: 'อนุญาตแล้ว', tone: 'success' },
  denied: { label: 'ถูกบล็อก', tone: 'error' },
  prompt: { label: 'ยังไม่ได้ตั้งค่า', tone: 'warning' },
  unsupported: { label: 'อุปกรณ์ไม่รองรับ', tone: 'grey' },
  unknown: { label: 'ยังไม่ทราบสถานะ', tone: 'grey' },
};

const BROWSER_LABEL = {
  chrome: 'Chrome',
  edge: 'Edge',
  firefox: 'Firefox',
  samsung: 'Samsung Internet',
  safari: 'Safari',
  other: 'เบราว์เซอร์',
};

// ตรวจอุปกรณ์/เบราว์เซอร์/โหมด PWA เพื่อแสดงขั้นตอนเปิดสิทธิ์ที่ตรงกับเครื่องผู้ใช้
const detectEnvironment = () => {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ ปลอมตัวเป็น Mac แต่มีหน้าจอสัมผัส
  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isWindows = /Windows/i.test(ua);
  const isMac = !isIOS && /Macintosh/i.test(ua);
  const isPwa = Boolean(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone);

  let browser = 'other';
  if (/SamsungBrowser/i.test(ua)) browser = 'samsung';
  else if (/Edg(e|A|iOS)?\//i.test(ua)) browser = 'edge';
  else if (/Firefox|FxiOS/i.test(ua)) browser = 'firefox';
  else if (/CriOS|Chrome/i.test(ua)) browser = 'chrome';
  else if (/Safari/i.test(ua)) browser = 'safari';

  return { isIOS, isAndroid, isWindows, isMac, isPwa, browser };
};

const BROWSER_SETTINGS_URL = {
  chrome: (origin) => `chrome://settings/content/siteDetails?site=${encodeURIComponent(origin)}`,
  edge: (origin) => `edge://settings/content/siteDetails?site=${encodeURIComponent(origin)}`,
};

// คืนรายการขั้นตอน (ข้อความภาษาไทย) ตามสภาพแวดล้อม + ชื่อสิทธิ์
const buildSteps = (env, permissionTitle) => {
  const pick = `เลือก "${permissionTitle}" แล้วตั้งเป็น "อนุญาต"`;
  const back = 'กลับมาที่แอป/หน้านี้ แล้วกด "ตรวจสอบใหม่"';

  if (env.isIOS) {
    if (env.isPwa) {
      return [
        'เปิดแอป "การตั้งค่า" (Settings) ของ iPhone/iPad',
        'เลื่อนลงหาชื่อแอปที่ติดตั้งไว้ (ไอคอนบนหน้าจอหลัก) แล้วแตะเข้าไป',
        pick,
        back,
      ];
    }
    return [
      'เปิดแอป "การตั้งค่า" (Settings) → Safari',
      'เลื่อนลงไปที่ "การตั้งค่าสำหรับเว็บไซต์" (Settings for Websites)',
      pick,
      'หรือแตะ "aA" ที่แถบที่อยู่ใน Safari → "การตั้งค่าเว็บไซต์" → เลือกสิทธิ์',
      back,
    ];
  }

  if (env.isAndroid) {
    if (env.isPwa) {
      return [
        'กดค้างที่ไอคอนแอปบนหน้าจอหลัก → แตะ ⓘ "ข้อมูลแอป" (App info)',
        'แตะ "สิทธิ์" (Permissions)',
        pick,
        back,
      ];
    }
    if (env.browser === 'firefox') {
      return ['แตะเมนู ⋮ → การตั้งค่า → การตั้งค่าเว็บไซต์ (Site permissions)', pick, back];
    }
    if (env.browser === 'samsung') {
      return ['แตะเมนู ☰ → การตั้งค่า → ไซต์และดาวน์โหลด → สิทธิ์ของไซต์', pick, back];
    }
    return [
      'แตะไอคอนแม่กุญแจ/ปุ่มปรับแต่งด้านซ้ายของชื่อเว็บที่แถบที่อยู่',
      'แตะ "สิทธิ์" (Permissions)',
      pick,
      back,
    ];
  }

  // Windows / Mac / อื่นๆ (เดสก์ท็อป)
  if (env.isPwa) {
    return [
      'กดไอคอน ⋮ (หรือคลิกขวา) ที่แถบชื่อของหน้าต่างแอป → "ข้อมูลแอป" (App info) / "การตั้งค่าแอป"',
      'เลือก "ตั้งค่าสิทธิ์" (Permissions) หรือ "การตั้งค่าไซต์"',
      pick,
      back,
    ];
  }
  if (env.browser === 'firefox') {
    return ['คลิกไอคอนแม่กุญแจที่แถบที่อยู่ → การเชื่อมต่อที่ปลอดภัย → ล้างค่า/ตั้งสิทธิ์', pick, back];
  }
  if (env.browser === 'safari') {
    return [
      'เมนู Safari → การตั้งค่า (Settings) → เว็บไซต์ (Websites)',
      'เลือกหัวข้อสิทธิ์ทางซ้าย แล้วหาเว็บนี้ตั้งเป็น "อนุญาต"',
      back,
    ];
  }
  return [
    'คลิกไอคอนแม่กุญแจ (หรือปุ่มปรับแต่ง) ด้านซ้ายของชื่อเว็บที่แถบที่อยู่',
    `สลับ "${permissionTitle}" เป็น "อนุญาต" (หรือคลิก "การตั้งค่าไซต์")`,
    'ถ้าระบบขอ ให้กด "โหลดหน้าใหม่" (Reload)',
    back,
  ];
};

const queryPermission = async (name) => {
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const result = await navigator.permissions.query({ name });
    return result.state;
  } catch (error) {
    return 'unknown';
  }
};

const readNotificationStatus = async () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission === 'default' ? 'prompt' : Notification.permission;
};

const requestNotification = async () => {
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    await subscribeToPush().catch(() => {});
  }
};

const requestLocation = () =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(() => resolve(), () => resolve());
  });

const requestCamera = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    // ผู้ใช้ปฏิเสธ — สถานะจะอัปเดตตอนอ่านใหม่
  }
};

const PERMISSIONS = [
  {
    key: 'notification',
    title: 'การแจ้งเตือน',
    desc: 'รับแจ้งเตือนอนุมัติ/คำขอต่างๆ จากระบบ',
    icon: NotificationIcon,
    supported: () => 'Notification' in window,
    read: readNotificationStatus,
    request: requestNotification,
  },
  {
    key: 'location',
    title: 'ตำแหน่งที่ตั้ง (GPS)',
    desc: 'ใช้ตรวจสอบสถานที่ตอนบันทึกเวลาเข้า-ออกงาน',
    icon: LocationIcon,
    supported: () => 'geolocation' in navigator,
    read: () => queryPermission('geolocation'),
    request: requestLocation,
  },
  {
    key: 'camera',
    title: 'กล้อง',
    desc: 'ใช้ถ่ายรูปตอนลงเวลาและแนบเอกสาร',
    icon: CameraIcon,
    supported: () => Boolean(navigator.mediaDevices?.getUserMedia),
    read: () => queryPermission('camera'),
    request: requestCamera,
  },
];

const toneColor = (tone) => (tone === 'grey' ? 'text.secondary' : `${tone}.main`);

const getEnvLabel = (env) => {
  const os = env.isIOS ? 'iOS' : env.isAndroid ? 'Android' : env.isWindows ? 'Windows' : env.isMac ? 'macOS' : 'อุปกรณ์';
  return `${os} · ${env.isPwa ? 'แอป (PWA)' : BROWSER_LABEL[env.browser]}`;
};

function PermissionCard({ permission, status, busy, onRequest, onHelp }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = permission.icon;
  const StatusIcon = { granted: CheckIcon, denied: BlockIcon }[status] || HelpIcon;

  let action = null;
  if (status === 'denied') {
    action = (
      <Button fullWidth variant="contained" color="error" onClick={onHelp}>
        ดูวิธีเปิดสิทธิ์
      </Button>
    );
  } else if (status === 'prompt' || status === 'unknown') {
    action = (
      <Button
        fullWidth
        variant="contained"
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        onClick={onRequest}
      >
        {status === 'prompt' ? 'เปิดใช้งาน' : 'ขออนุญาต'}
      </Button>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: status === 'denied' ? 'error.light' : 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            color: toneColor(meta.tone),
            bgcolor: 'action.hover',
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} noWrap>
            {permission.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            {permission.desc}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5, color: toneColor(meta.tone) }}>
        <StatusIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={600}>
          {meta.label}
        </Typography>
      </Stack>

      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Paper>
  );
}

function InstallCard({ env }) {
  const { canInstall, install } = usePwaInstall();
  const installed = isStandalone();

  let hint = 'เปิดเมนูของเบราว์เซอร์ แล้วเลือก "ติดตั้งแอป" หรือ "เพิ่มลงในหน้าจอหลัก" (Add to Home screen)';
  if (env.isIOS) {
    hint = 'แตะปุ่มแชร์ (ไอคอนสี่เหลี่ยมมีลูกศรขึ้น) ใน Safari แล้วเลือก "เพิ่มไปยังหน้าจอโฮม" (Add to Home Screen)';
  }

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
    >
      <Stack direction="row" spacing={1.75} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            color: installed ? 'success.main' : 'primary.main',
            bgcolor: 'action.hover',
          }}
        >
          <InstallIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700}>ติดตั้งแอปลงเครื่อง</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            เปิดจากหน้าจอหลักได้เหมือนแอปทั่วไป และรับแจ้งเตือนได้เสถียรขึ้น
          </Typography>
        </Box>
      </Stack>

      {installed ? (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5, color: 'success.main' }}>
          <CheckIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={600}>
            ติดตั้งแล้ว (กำลังใช้งานในโหมดแอป)
          </Typography>
        </Stack>
      ) : canInstall ? (
        <Button fullWidth variant="contained" startIcon={<InstallIcon />} sx={{ mt: 1.5 }} onClick={install}>
          ติดตั้งแอป
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

export default function PermissionSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [helpTarget, setHelpTarget] = useState(null);
  const env = detectEnvironment();
  const settingsUrl = BROWSER_SETTINGS_URL[env.browser]?.(window.location.origin);
  const showSettingsUrl = settingsUrl && !env.isPwa && !env.isIOS && !env.isAndroid;

  const refresh = useCallback(async () => {
    const entries = await Promise.all(
      PERMISSIONS.map(async (p) => [p.key, p.supported() ? await p.read() : 'unsupported']),
    );
    setStatuses(Object.fromEntries(entries));
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    // ผู้ใช้มักออกไปเปิดสิทธิ์ในหน้าตั้งค่าแล้วสลับกลับมา — อ่านสถานะใหม่ให้เอง
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  const handleRequest = async (permission) => {
    setBusyKey(permission.key);
    await permission.request();
    await refresh();
    setBusyKey(null);
  };

  const supportedCount = PERMISSIONS.filter((p) => statuses[p.key] !== 'unsupported').length;
  const grantedCount = PERMISSIONS.filter((p) => statuses[p.key] === 'granted').length;
  const deniedCount = PERMISSIONS.filter((p) => statuses[p.key] === 'denied').length;
  const allGranted = supportedCount > 0 && grantedCount === supportedCount;

  let summaryTitle = `เปิดใช้งานแล้ว ${grantedCount} จาก ${supportedCount} รายการ`;
  let summaryTone = 'primary';
  if (allGranted) {
    summaryTitle = 'พร้อมใช้งานครบทุกฟังก์ชัน';
    summaryTone = 'success';
  } else if (deniedCount > 0) {
    summaryTone = 'warning';
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', pb: 4 }}>
      {/* สรุปภาพรวม */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 4,
          color: '#fff',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette[summaryTone].main}, ${theme.palette[summaryTone].dark})`,
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1 }}>
          สิทธิ์การเข้าถึงของเว็บ
        </Typography>
        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3 }}>
          {loading ? 'กำลังตรวจสอบ...' : summaryTitle}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={supportedCount ? (grantedCount / supportedCount) * 100 : 0}
          sx={{
            my: 1.5,
            height: 8,
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.3)',
            '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 4 },
          }}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Chip
            size="small"
            icon={<DeviceIcon sx={{ color: '#fff !important' }} />}
            label={getEnvLabel(env)}
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.22)' }}
          />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          >
            ตรวจสอบใหม่
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {PERMISSIONS.map((p) => (
          <PermissionCard
            key={p.key}
            permission={p}
            status={statuses[p.key] || 'unknown'}
            busy={busyKey === p.key}
            onRequest={() => handleRequest(p)}
            onHelp={() => setHelpTarget(p)}
          />
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <InstallCard env={env} />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
        หากสิทธิ์ถูกบล็อก เว็บไม่สามารถขอซ้ำเองได้ ต้องเปิดที่การตั้งค่าของเบราว์เซอร์/อุปกรณ์
      </Typography>

      <Dialog
        open={Boolean(helpTarget)}
        onClose={() => setHelpTarget(null)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: 'hidden' } }}
      >
        {helpTarget && (
          <>
            {/* หัวโมดัล */}
            <Box
              sx={{
                position: 'relative',
                px: 3,
                pt: 3,
                pb: 2.5,
                color: '#fff',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              }}
            >
              <IconButton
                size="small"
                aria-label="ปิด"
                onClick={() => setHelpTarget(null)}
                sx={{ position: 'absolute', top: 10, right: 10, color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  mb: 1.5,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.22)',
                }}
              >
                <helpTarget.icon />
              </Box>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1 }}>
                สิทธิ์ถูกบล็อก
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3 }}>
                วิธีเปิดสิทธิ์ “{helpTarget.title}”
              </Typography>
              <Chip
                size="small"
                icon={<DeviceIcon sx={{ color: '#fff !important' }} />}
                label={getEnvLabel(env)}
                sx={{ mt: 1.25, color: '#fff', bgcolor: 'rgba(255,255,255,0.22)' }}
              />
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                เว็บไม่สามารถเปิดหน้าตั้งค่าให้เองได้ ทำตามขั้นตอนด้านล่าง แล้วกลับมากดปุ่มตรวจสอบ
              </Typography>

              <Box>
                {buildSteps(env, helpTarget.title).map((step, index, steps) => {
                  const isLast = index === steps.length - 1;
                  return (
                    <Stack key={step} direction="row" spacing={1.5}>
                      <Stack alignItems="center" sx={{ width: 28, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#fff',
                            bgcolor: isLast ? 'success.main' : 'primary.main',
                          }}
                        >
                          {isLast ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                        </Box>
                        {!isLast && <Box sx={{ flex: 1, width: 2, my: 0.5, bgcolor: 'divider' }} />}
                      </Stack>
                      <Typography variant="body2" sx={{ pt: 0.4, pb: isLast ? 0 : 2 }}>
                        {step}
                      </Typography>
                    </Stack>
                  );
                })}
              </Box>

              {showSettingsUrl && (
                <Paper
                  elevation={0}
                  sx={{ mt: 2.5, p: 1.5, borderRadius: 2.5, bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" color="text.secondary">
                    ทางลัด: คัดลอกลิงก์แล้ววางในแถบที่อยู่
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all', my: 0.5 }}>
                    {settingsUrl}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => navigator.clipboard?.writeText(settingsUrl).catch(() => {})}
                  >
                    คัดลอกลิงก์
                  </Button>
                </Paper>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
              <Button fullWidth variant="outlined" color="inherit" onClick={() => setHelpTarget(null)}>
                ปิด
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={async () => {
                  await refresh();
                  setHelpTarget(null);
                }}
              >
                ตรวจสอบใหม่
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
